/**
 * Feature Toggles
 * * Controls what feature is supported in a business line
 * * Usage is described in the `Purpose` Section
 */
export enum Feature {
  // General
  HideContactBusinessPartnerInDialog = 'HideContactBusinessPartnerInDialog',
  ForceCenterTextInOtpErrorDialog = 'ForceCenterTextInOtpErrorDialog',
  /**
   * TODO: Remove this feature toggle and replace with business target check:
   * if (BUSINESS_TARGET === BusinessTarget.ShopeePartner || BUSINESS_TARGET === BusinessTarget.MyStock)
   * This is a Shopee Partner/MyStock specific feature for password migration.
   * The logic should early return if not applicable to the current business target.
   */
  CallCheckPasswordMigrateApi = 'CallCheckPasswordMigrateApi', // whether to call check_password_migrate api
  LoginHideSignupButton = 'LoginHideSignupButton', // Hiding the signup button in login page, but not the signup route.
  LoginTermsOfService = 'LoginTermsOfService',
  HomepageRedirection = 'HomepageRedirection',
  ShowPoweredByShopeeText = 'ShowPoweredByShopeeText',
  EnableLoginTimeout = 'EnableLoginTimeout',
  ValidateAuthenticationRedirectionConfig = 'ValidateAuthenticationRedirectionConfig', // Validate Next URL on entry
  SupportZaloOtp = 'SupportZaloOtp',
  SupportViberOtp = 'SupportViberOtp',
  WebTracking = 'WebTracking',
  AppTracking = 'AppTracking',
  LanguageSelector = 'LanguageSelector',
  AccountSuspendedUseHelpCenterLink = 'AccountSuspendedUseHelpCenterLink',
  HideShopeeLogo = 'HideShopeeLogo', // hide shopee logo. Currently used in one credential flows

  // Login by password
  LoginByPasswordEntrance = 'LoginByPasswordEntrance', // Purpose: allow login by password
  LoginByPasswordAllowEmail = 'LoginByPasswordAllowEmail', // Purpose: allow login using email + password
  LoginByPasswordAllowPhone = 'LoginByPasswordAllowPhone', // Purpose: allow login using phone + password
  LoginByPasswordRestrictPasswordLength = 'LoginByPasswordRestrictPasswordLength',
  LoginByPasswordNewUserHint = 'LoginByPasswordNewUserHint',
  LoginByPasswordCheckPasswordExistApi = 'LoginByPasswordCheckPasswordExistApi',
  LoginByPasswordShowResetPasswordLinkInSmallDevice = 'LoginByPasswordShowResetPasswordLinkInSmallDevice',
  LoginByPasswordSetNewPasswordOnInitialLogin = 'LoginByPasswordSetNewPasswordOnInitialLogin', // Purpose: set new password on initial login
  LoginByPasswordSkipShopeeAccountConfirmation = 'LoginByPasswordSkipShopeeAccountConfirmation', // Purpose: skip shopee account confirmation page
  LoginByPasswordLayoutRefactor = 'LoginByPasswordLayoutRefactor',
  LoginByPasswordOneCredentialLayoutRefactor = 'LoginByPasswordOneCredentialLayoutRefactor',
  LoginByPasswordShowLoginTip = 'LoginByPasswordShowLoginTip',

  // Login by OTP
  LoginByOtpEntrance = 'LoginByOtpEntrance', // Purpose: allow login by OTP
  LoginByOtpAllowEmail = 'LoginByOtpAllowEmail', // Purpose: allow login using email + OTP
  LoginByOtpAllowPhone = 'LoginByOtpAllowPhone', // Purpose: allow login using phone + OTP
  LoginByOtpNotRegisteredRedirectToSignup = 'LoginByOtpNotRegisteredRedirectToSignup', // Purpose: redirect to signup page if account not registered
  LoginByOtpSupportWAL = 'LoginByOtpSupportWAL', // Purpose: support WAL for login by OTP

  // Login by Biometric
  // support biometrics login
  BiometricsV1 = 'BiometricsV1', // Purpose: support biometrics login
  // support biometrics login + passcode login, ToB Account SDK has breaking changes comparing with v1
  // v2 toggle is not only related feature greyscale, it means what version of ToB Account SDK is used
  // if business app doesn't require passcode feature but it integrates with latest ToB Account SDK, we should also enable this toggle
  // for feature greyscale, we should use CCMS feature toggle instead
  BiometricsV2 = 'BiometricsV2', // Purpose: support biometrics login + passcode login
  LoginByBiometricsSupportWAL = 'LoginByBiometricsSupportWAL', // Purpose: support WAL for login by biometrics
  LoginByBiometricsEmailOtp = 'LoginByBiometricsEmailOtp', // Purpose: support email + OTP for login by biometrics

  // Quick Login
  QuickLogin = 'QuickLogin',

  // Signup by OTP
  SignupByOtpEntrance = 'SignupByOtpEntrance', // Purpose: allow signup by OTP
  SignupByOtpEnableOneCredential = 'SignupByOtpEnableOneCredential', // Purpose: enable one credential for signup by OTP
  SignupByOtpAllowEmail = 'SignupByOtpAllowEmail', // Purpose: allow signup using email + OTP
  SignupByOtpAllowPhone = 'SignupByOtpAllowPhone', // Purpose: allow signup using phone + OTP
  SignupByOtpRequireAcceptTermsAndService = 'SignupByOtpRequireAcceptTermsAndService',
  SignupByOtpRequirePassword = 'SignupByOtpRequirePassword', // Purpose: require set password for signup by OTP
  SignupByOtpCustomiseResendOtpMessage = 'SignupByOtpCustomiseResendOtpMessage',
  SignupByOtpLoginUponAlreadyRegisteredWithPhone = 'SignupByOtpLoginUponAlreadyRegisteredWithPhone', // Purpose: directly login users if they are already registered with Phone OTP
  SignupByOtpLoginUponAlreadyRegisteredWithEmail = 'SignupByOtpLoginUponAlreadyRegisteredWithEmail', // Purpose: directly login users if they are already registered with Email OTP
  SignupByOtpHideSignupTip = 'SignupByOtpHideSignupTip',
  SignupByOtpSkipAlreadyRegistered = 'SignupByOtpSkipAlreadyRegistered',
  SignupByOtpSkipSignupSuccess = 'SignupByOtpSkipSignupSuccess',
  SignupByOtpDisableReclaimPhone = 'SignupByOtpDisableReclaimPhone', // Purpose: disable phone reclaim in signup by OTP
  SignupByOtpSupportWAL = 'SignupByOtpSupportWAL', // Purpose: support WAL for signup by OTP
  SignupByOtpLayoutRefactor = 'SignupByOtpLayoutRefactor',

  // Reset password
  ResetPasswordEntrance = 'ResetPassword', // Purpose: allow reset password
  ResetPasswordFromLinkEntrance = 'ResetPasswordFromLinkEntrance', // Purpose: allow reset password from link
  ResetPasswordFromPinVerification = 'ResetPasswordFromPinVerification', // Purpose: allow reset password from FMS PIN/face verification (skips OTP)
  ResetPasswordShowHelpText = 'ResetPasswordShowHelpText',
  ResetPasswordByPhone = 'ResetPasswordByPhone', // Purpose: allow reset password by Phone OTP
  ResetPasswordByEmail = 'ResetPasswordByEmail', // Purpose: allow reset password by Email OTP
  ResetPasswordCallResetPasswordInit = 'ResetPasswordCallResetPasswordInit',
  ResetPasswordSuccessCountdownPage = 'ResetPasswordSuccessCountdownPage',
  ResetPasswordHandledByBusinessPartner = 'ResetPasswordHandledByBusinessPartner', // Purpose: let the user know that reset password is handled by business partner
  ResetPasswordOnShopeeJumpToToC = 'ResetPasswordOnShopeeJumpToToC',
  ResetPasswordSupportWAL = 'ResetPasswordSupportWAL',

  // Shopeepay specific
  ShopeePayLoginByPassword = 'ShopeePayLoginByPassword', // allow route handling for ShopeePay login (/login)
  ShopeePayChangePassword = 'ShopeePayChangePassword', // allow route handling for ShopeePay change password (/changePass)
  LoginByPasswordRedirectToShopeePay = 'LoginByPasswordRedirectToShopeePay', // for legacy shopeepay app that goes to `shopee-partner.business.accounts.shopee.<cid>/login`, auto redirect them to `pay.business.accounts.shopee.<cid>/login` domain
  ShopeePayMigrationBanner = 'ShopeePayMigrationBanner',

  // Shopee Partner specific
  ShopeePartnerWeb = 'ShopeePartnerWeb',
  ShopeePartnerDesktopBackground = 'ShopeePartnerDesktopBackground',
  ShopeePartnerHashPassword = 'ShopeePartnerHashPassword', // For TH/VN in shopee partner, password field is not hashed, as in the past it supported ShopeePay. Long term goal is to hash the password field for whole of shopee partner remove this toggle

  // WebView
  WebViewNotifyLoginError = 'WebViewNotifyLoginError',
  WebViewLogoutAfterCredentialChange = 'WebViewLogoutAfterCredentialChange', // Notify webview to log out after credential change in flows like add/change email, phone or password
  OpenAccountDeleteLinkAsExternalUrl = 'OpenAccountDeleteLinkAsExternalUrl', // Open link as external URL
  OpenAccountDeleteIosLinkAsAppRL = 'OpenAccountDeleteIosLinkAsAppRL', // Open link as appRL https://confluence.shopee.io/display/SPAT/Navigate+AppRL
  OpenAccountDeleteAndroidLinkAsAppRL = 'OpenAccountDeleteAndroidLinkAsAppRL', // Open link as appRL https://confluence.shopee.io/display/SPAT/Navigate+AppRL
  NotifyRefreshToken = 'NotifyRefreshToken', // Returning nonce after changing credentials, , e.g change password
  // If both toggles are not true, will not call either method
  // NOTE: This is for older app versions. If this is true, will send using older and unsafe method of notifyDidFinishToBLogin
  // NOTE: THIS TOGGLE SHOULD NEVER BE TRUE BY DEFAULT. If business starts supporting, they should use the new method of notifyRefreshToBToken.
  // At most, this toggle should be enabled up to a specified version.
  NotifyRefreshTokenLegacy = 'NotifyRefreshTokenLegacy',
  OpenSignupTipFormAsExternalUrl = 'OpenSignupTipFormAsExternalUrl', // Open SignupByOtpTip form link as external url
  WebViewLoginSuccessfullySendFromSource = 'WebViewLoginSuccessfullySendFromSource', // whether the webViewLoginSuccessfully() command should send the fromSource parameter
  WebViewNotifySignupSuccess = 'WebViewNotifySignupSuccess', // whether the webViewNotifySignupSuccess() command should be called
  WebViewABTestSupported = 'WebViewABTestSupported', // whether the abtest experiment should be supported in webview
  // Account pages
  UserAccountEntrance = 'UserAccountEntrance', // Purpose: enable user account settings page
  UserManagePhoneNumber = 'UserManagePhoneNumber', // Purpose: Phone number shown in user account settings page
  UserManageEmail = 'UserManageEmail', // Purpose: Email shown in user account settings page
  UserManageUsername = 'UserManageUsername', // Purpose: Username shown in user account settings page
  UserManagePassword = 'UserManagePassword', // Purpose: Password entry shown in user account settings page
  UserManageToCPassword = 'UserManageToCPassword', // Purpose: enable user to manage ToC password
  UserShowToCPhoneInfo = 'UserShowToCPhoneInfo',
  UserShowToCHelpFooter = 'UserShowToCHelpFooter',
  UserManageToCLinkedEmail = 'UserManageToCLinkedEmail', // Purpose: enable user to manage ToC linked email
  AdminManagePhoneNumber = 'AdminManagePhoneNumber', // Purpose: enable admin to manage phone number (for BUSINESS_TARGET=shopee-partner only)
  AdminManageEmail = 'AdminManageEmail', // Purpose: enable admin to manage email (for BUSINESS_TARGET=shopee-partner only)
  AdminManageUsername = 'AdminManageUsername', // Purpose: enable admin to manage username (for BUSINESS_TARGET=shopee-partner only)
  AdminManagePassword = 'AdminManagePassword', // Purpose: enable admin to manage password (for BUSINESS_TARGET=shopee-partner only)
  AccountDeleteEntranceIOS = 'AccountDeleteEntranceIOS', // Purpose: enable account deletion entrance on account settings page (IOS only)
  AccountDeleteEntranceAndroid = 'AccountDeleteEntranceAndroid', // Purpose: enable account deletion entrance on account settings page (Android only)
  UnmaskUserPhone = 'UnmaskUserPhone',
  UnmaskUserEmail = 'UnmaskUserEmail',
  UserCredentialsCommit = 'UserCredentialsCommit',

  // User phone
  UserPhoneEntrance = 'UserPhoneEntrance', // Purpose: enable update phone entrance
  UserAddPhone = 'UserAddPhone', // Purpose: enable add phone operation
  UserChangePhone = 'UserChangePhone', // Purpose: enable change phone operation
  UserChangePhoneSupportWAL = 'UserChangePhoneSupportWAL', // Purpose: support WAL for change phone operation
  CallChangePhoneInitApiInProfilePage = 'CallChangePhoneInitApiInProfilePage',
  ChangePhoneUseNativeVerification = 'ChangePhoneUseNativeVerification',

  // User email
  UserEmailEntrance = 'UserEmailEntrance', // Purpose: enable update email entrance
  UserAddEmail = 'UserAddEmail', // Purpose: enable add email operation
  UserChangeEmail = 'UserChangeEmail', // Purpose: enable change email operation
  CallChangeEmailInitApiInProfilePage = 'CallChangeEmailInitApiInProfilePage',
  ChangeEmailUseNativeVerification = 'ChangeEmailUseNativeVerification',

  // User password
  UserPasswordEntrance = 'UserPasswordEntrance', // Purpose: enable update password entrance
  UserPasswordForgetPrompt = 'UserPasswordForgetPrompt',
  UserPasswordWebLogoutAfterCredentialChange = 'UserPasswordWebLogoutAfterCredentialChange',
  CallChangePasswordInitApiInProfilePage = 'CallChangePasswordInitApiInProfilePage',
  ChangePasswordUseNativeVerification = 'ChangePasswordUseNativeVerification',

  // User account linkage
  UserAccountLinkage = 'UserAccountLinkage',

  // One Credential
  OneCredential = 'OneCredential', // Purpose: enable one credential feature
  OneCredentialAllowPhone = 'OneCredentialAllowPhone', // Purpose: allow account one credential update using Phone OTP
  OneCredentialAllowEmail = 'OneCredentialAllowEmail', // Purpose: allow account one credential update using Email OTP
  OneCredentialSupportToBAccount = 'OneCredentialSupportToBAccount', // Purpose: if ToC One credential solution is supported, it checks whether we support ToB Accounts as well
  OneCredentialAcceptInvitation = 'OneCredentialAcceptInvitation',
  OneCredentialLoginShowNoLinkedToBAccountError = 'OneCredentialLoginShowNoLinkedToBAccountError',
  OneCredentialSignupSkipCreateAccountPage = 'OneCredentialSignupSkipCreateAccountPage',
  OneCredentialOptional = 'OneCredentialOptional', // Purpose: Enable Optional One Credential Solution

  // One Credential Migration
  OneCredentialMigrationEntrance = 'OneCredentialMigrationEntrance', // Purpose: enable one credential update entrance
  OneCredentialMigrationAllowStaffToChangePhoneAndEmail = 'OneCredentialMigrationAllowStaffToChangePhoneAndEmail', // Purpose: allow staff to change phone and email in one credential migration (for BUSINESS_TARGET=shopee-partner only)
  OneCredentialMigrationDisallowNotifyAdmin = 'OneCredentialMigrationDisallowNotifyAdmin', // Purpose: disallow notify admin in one credential migration (for BUSINESS_TARGET=shopee-partner only)
  OneCredentialMigrationCallCheckAuthKeyAvailable = 'OneCredentialMigrationCallCheckAuthKeyAvailable',
  OneCredentialMigrationBypassOtp = 'OneCredentialMigrationBypassOtp',
  OneCredentialMigrationNotifyMigrationSuccess = 'OneCredentialMigrationNotifyMigrationSuccess',
  OneCredentialMigrationLinkingNoticePopup = 'OneCredentialMigrationLinkingNoticePopup',
  OneCredentialShouldCreateToBAccount = 'OneCredentialShouldCreateToBAccount',
  OneCredentialMigrationSkipAccountUpdate = 'OneCredentialMigrationSkipAccountUpdate',
  OneCredentialMigrationSupportWAL = 'OneCredentialMigrationSupportWAL', // Purpose: support WAL for one credential migration

  // OneCredentialDelink
  OneCredentialDelinkEntrance = 'OneCredentialDelinkEntrance', // Purpose: enable one credential delink entrance
  OneCredentialDelinkAllowPhone = 'OneCredentialDelinkAllowPhone', // Purpose: allow account one credential delink using Phone OTP
  OneCredentialDelinkAllowEmail = 'OneCredentialDelinkAllowEmail', // Purpose: allow account one credential delink using Email OTP
  OneCredentialDelinkSetPasswordEnabled = 'OneCredentialDelinkSetPasswordEnabled', // Purpose: allow set password in one credential delink flow
  OneCredentialDelinkSupportWAL = 'OneCredentialDelinkSupportWAL', // Purpose: support WAL for one credential delink flow
  OneCredentialDelinkAllowUsername = 'OneCredentialDelinkAllowUsername', // Purpose: allow account one credential delink using Username

  // One Credential Switch ToC Link
  OneCredentialSwitchToCLinkAdminEnabled = 'OneCredentialSwitchToCLinkAdminEnabled', // Purpose: allow admin to switch to ToC linkage in one credential switch ToC link flow
  OneCredentialSwitchToCLinkStaffEnabled = 'OneCredentialSwitchToCLinkStaffEnabled', // Purpose: allow staff to switch to ToC linkage in one credential switch ToC link flow

  // Thirdparty Integration
  ThirdPartyBindingSharedServiceEntrance = 'ThirdPartyBindingSharedServiceEntrance', // Purpose: enable third party binding shared service page
  ThirdpartyIntegrationSupportShopeeInApp = 'ThirdpartyIntegrationSupportShopeeInApp', // Purpose: enable Third Party Login With Shopee integration in app
  ThirdpartyIntegrationSupportShopeeInWeb = 'ThirdpartyIntegrationSupportShopeeInWeb', // Purpose: enable Third Party Login With shopee integration in web
  ThirdpartyIntegrationSupportGoogleInApp = 'ThirdpartyIntegrationSupportGoogleInApp', // Purpose: enable Third Party Login With Google integration in app
  ThirdpartyIntegrationSupportGoogleInWeb = 'ThirdpartyIntegrationSupportGoogleInWeb', // Purpose: enable Third Party Login With Google integration in web
  ThirdpartyIntegrationSupportFacebookInApp = 'ThirdpartyIntegrationSupportFacebookInApp', // Purpose: enable Third Party Login With Facebook integration in app
  ThirdpartyIntegrationSupportFacebookInWeb = 'ThirdpartyIntegrationSupportFacebookInWeb', // Purpose: enable Third Party Login With Facebook integration in web
  ThirdpartyIntegrationSupportAppleInApp = 'ThirdpartyIntegrationSupportAppleInApp', // Purpose: enable Third Party Login With Apple integration in app
  ThirdpartyIntegrationSupportAppleInWeb = 'ThirdpartyIntegrationSupportAppleInWeb', // Purpose: enable Third Party Login With Apple integration in web

  // IVS
  IvsIntegration = 'IvsIntegration',
  VerifyPasswordSharedService = 'VerifyPasswordSharedService',
  IvsSharedService = 'IvsSharedService',
  IvsHideShopeeBranding = 'IvsHideShopeeBranding',
  VerifyOtpSharedService = 'VerifyOtpSharedService', // Purpose: enable OTP verification shared service

  // Temporary toggles
  /**
   * For each business line, these toggles should be MUTUALLY EXCLUSIVE per region (only 1 toggle can take effect per region)
   */
  SPXRebrandUI = 'SPXRebrandUI', // Temporary toggle for SPX Rebranding
  SPXDebrandUI = 'SPXDebrandUI',
  SPXOrangeBackgroundUI = 'SPXOrangeBackgroundUI',
  SendLanguageInSendOtp = 'SendLanguageInSendOtp', // Temporary toggle for sending language parameter to the send_otp API

  // Tob One Credential
  TobOneCredential = 'TobOneCredential',

  // Relogin Page
  ReloginPageEntrance = 'ReloginPageEntrance',

  // Disable Relogin Info
  DisableReloginInfoEntrance = 'DisableReloginInfoEntrance',
}

export enum RuleType {
  GreyscaleDeviceFingerprint = 2,
}
